import { Component, type ErrorInfo, type ReactNode } from "react";

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optionaler Fallback statt des Standard-Bildschirms. */
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  message: string | null;
}

/**
 * App-weite Fehlergrenze. Ohne sie reißt ein Render-/Effekt-Throw (z.B. ein
 * fehlgeschlagener MapLibre-WebGL-Init) den gesamten React-Root mit und der
 * Nutzer sieht eine weiße Seite. Hier fangen wir den Fehler lokal ab und
 * zeigen einen neutralen Wiederherstellungs-Screen.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { hasError: false, message: null };

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    const message = error instanceof Error ? error.message : "Unbekannter Fehler";
    return { hasError: true, message };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Sichtbar in der Browser-Konsole für Diagnose; kein externes Logging.
    console.error("ErrorBoundary hat einen Fehler abgefangen:", error, info.componentStack);
  }

  private handleReload = (): void => {
    this.setState({ hasError: false, message: null });
    window.location.reload();
  };

  override render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    return (
      <div
        style={{
          minHeight: "60vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 16,
          padding: 48,
          textAlign: "center",
          color: "var(--color-text-muted)",
        }}
      >
        <div style={{ fontSize: 40 }}>⚠️</div>
        <div style={{ fontSize: 18, fontWeight: 600, color: "var(--color-text)" }}>
          Es ist ein unerwarteter Fehler aufgetreten.
        </div>
        <div style={{ fontSize: 13, maxWidth: 480 }}>
          Die Ansicht konnte nicht dargestellt werden. Bitte laden Sie die Seite neu.
          {this.state.message ? ` (${this.state.message})` : ""}
        </div>
        <button className="btn-primary" onClick={this.handleReload}>
          Seite neu laden
        </button>
      </div>
    );
  }
}
